<template>
	<div
		class="ui-date-picker"
		:id="datePickerUniqueId"
		ref="datePickerWrapper"
	>
		<slot :isOpen="isOpen" />
	</div>
</template>

<script setup lang="ts">
	import { onClickOutside } from '@vueuse/core';
	import Datepicker from '@/assets/js/datepicker';
	import { convertDate } from '~/helpers/date';
	import { useNotificationStore } from '~/store/notification.store';

	const emit = defineEmits(['update:modelValue', 'blur', 'chooseDate']);
	const datePickerWrapper = ref<HTMLElement | null>(null);
	let datepickerInstance: any = null;
	const isOpen = ref(false);
	let isFromOpen = false;

	const props = withDefaults(
		defineProps<{
			modelValue?: Date | Date[] | string;
			datePickerUniqueId: string;
			isNeedFormat?: boolean;
			isDontNeedPosibleOpen?: boolean;
			maxValue?: Date;
			minValue?: Date;
			disabled?: boolean;
			disabledText?: string;
			isRanged?: boolean;
			calendarCount?: number;
			multiple?: boolean;
			firstMonthByCurrent?: number;
			isPosibleClear?: boolean;
		}>(),
		{
			datePickerUniqueId: 'ui-date-picker',
			isNeedFormat: true,
			isRanged: false,
			calendarCount: 1,
			multiple: false,
			firstMonthByCurrent: 0,
			isPosibleClear: false
		}
	);

	const clearBtnHtml = props?.isPosibleClear ? '<div class="clear-btn" @click="testHandler">сбросить</div>' : '';

	const getCalendarElement = (): HTMLElement | null => {
		if (!datePickerWrapper.value) return null;
		return datePickerWrapper.value.querySelector('.datepicker__wrapper') as HTMLElement;
	};

	const hideCalendar = () => {
		const calendarEl = getCalendarElement();
		if (calendarEl) {
			calendarEl.style.visibility = 'hidden';
		}
	};

	const updatePosition = () => {
		if (!datepickerInstance || !datePickerWrapper.value) return;

		const wrapper = datePickerWrapper.value;
		const rect = wrapper.getBoundingClientRect();
		const calendarEl = getCalendarElement();

		if (!calendarEl) return;

		const calendarWidth = calendarEl.offsetWidth;
		const calendarHeight = calendarEl.offsetHeight;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const offset = 5;
		const padding = 10;

		let top = rect.bottom + offset;
		let left = rect.left;

		if (left + calendarWidth > viewportWidth - padding) {
			left = viewportWidth - calendarWidth - padding;
		}

		if (left < padding) {
			left = padding;
		}

		if (top + calendarHeight > viewportHeight - padding) {
			top = rect.top - calendarHeight - offset;
		}
		if (top < padding) {
			top = padding;
		}

		calendarEl.style.top = `${top}px`;
		calendarEl.style.left = `${left}px`;
		calendarEl.style.visibility = 'visible';
	};

	onClickOutside(datePickerWrapper, () => {
		isOpen.value = false;
		hideCalendar();
		if (datepickerInstance) {
			if (typeof datepickerInstance.hide === 'function') {
				datepickerInstance.hide();
			} else if (typeof datepickerInstance.close === 'function') {
				datepickerInstance.close();
			}
		}
	});

	const changeHandler = (value: Date | [Date, Date]) => {
		if (!value) return;
		isOpen.value = false;
		hideCalendar();

		if (!Array.isArray(value) && props.isNeedFormat) {
			emit('update:modelValue', convertDate(value));
			setTimeout(() => {
				emit('blur', value);
				if (!isFromOpen) {
					emit('chooseDate', value);
				}
				isFromOpen = false;
			});
			return;
		}

		emit('update:modelValue', value);
		setTimeout(() => {
			emit('blur', value);
			if (!isFromOpen) {
				emit('chooseDate', value);
			}
			isFromOpen = false;
		});
	};

	const handleClearClick = (event: Event) => {
		isOpen.value = false;
		const target = event.target as HTMLElement;
		if (target.classList.contains('clear-btn')) {
			event.preventDefault();
			event.stopPropagation();
			clearDate();
		}
	};

	const clearDate = () => {
		emit('update:modelValue', props.isRanged ? [] : null);
		hideCalendar();
		if (datepickerInstance) {
			datepickerInstance.setValue('');
			datepickerInstance.hide();
		}
	};

	onMounted(() => {
		if (props.isDontNeedPosibleOpen) return;

		if (datePickerWrapper.value) {
			const targetElement = datePickerWrapper.value.firstElementChild as HTMLElement;
			if (!targetElement) return;

			datePickerWrapper.value.addEventListener('click', handleClearClick);

			datepickerInstance = new Datepicker(targetElement, {
				ranged: props.isRanged,
				multiple: props.multiple,
				onChange: changeHandler,
				isOpenDisabled: () => {
					if (props.disabled && props.disabledText) {
						useNotificationStore().addNewAlert(props.disabledText, 4000);
					}
					return props.disabled === true;
				},
				openOn: function () {
					hideCalendar();

					setTimeout(() => {
						isOpen.value = true;
						updatePosition();
					});

					const minDate = props.minValue ? new Date(props.minValue) : false;
					this._opts.max = props.maxValue ? new Date(props.maxValue) : false;
					this._opts.min = minDate ? new Date(minDate.setDate(minDate.getDate() - 1)) : false;

					isFromOpen = true;

					if (typeof props.modelValue === 'string') {
						this.addDate(new Date(props.modelValue));
						return new Date(props.modelValue);
					} else if (Array.isArray(props.modelValue)) {
						this.addDate(props.modelValue);
						return props.modelValue;
					}
					return null;
				},
				classNames: { node: 'ui-date-picker__wrapper' },
				weekStart: 1,
				i18n: {
					time: ['', '', ''],
					weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
					months: [
						'Январь',
						'Февраль',
						'Март',
						'Апрель',
						'Май',
						'Июнь',
						'Июль',
						'Август',
						'Сентябрь',
						'Октябрь',
						'Ноябрь',
						'Декабрь'
					]
				},
				getFirstMonthByCurrent: () => props.firstMonthByCurrent,
				templates: {
					container: [
						'<div class="datepicker__container">',
						`<% for (var i = ${props.firstMonthByCurrent}; i <= ${props.firstMonthByCurrent + props.calendarCount - 1}; i++) { %>`,
						'<div class="datepicker__pane">',
						'<%= renderHeader(i) %>',
						'<%= renderCalendar(i) %>',
						'</div>',
						'<% } %>',
						clearBtnHtml,
						'</div>'
					].join('')
				}
			});

			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
		}
	});

	onUnmounted(() => {
		if (datePickerWrapper.value) {
			datePickerWrapper.value.removeEventListener('click', handleClearClick);
		}

		window.removeEventListener('scroll', updatePosition, true);
		window.removeEventListener('resize', updatePosition);
		datepickerInstance = null;
	});
</script>

<style lang="scss" src="">
	.datepicker__container {
		display: flex;

		@media (max-width: $lg) {
			flex-direction: column;
		}
	}
	.ui-date-picker {
		display: flex;
		align-items: center;
		justify-content: center;

		.datepicker__daynum {
			font-size: 13px;
			padding: 7px 8px;
		}

		.is-disabled {
			opacity: 0.45;
			cursor: not-allowed !important;
		}

		.is-selected {
			background-color: var(--gray-10);
			color: var(--black);
			opacity: 1;
		}

		.is-selected__last {
			background-color: var(--primary);
			color: var(--white);
		}

		.is-selected__first {
			background-color: var(--calendar-active);
			color: var(--white);
		}

		.is-today {
			background-color: var(--calendar-current-day);
			border-radius: 50%;
			color: var(--calendar-active);
			opacity: 1;
		}

		.is-otherMonth {
			opacity: 0.5;
		}
	}
	.ui-date-picker__wrapper {
		width: 100%;
		.datepicker {
			&__container {
				display: flex;
				flex-direction: column;
				gap: 5px;

				.clear-btn {
					background: none;
					border: none;
					cursor: pointer;
					color: var(--secondary);
					font-size: 14px;
					display: flex;
					align-items: center;
					justify-content: flex-end;
					padding: 0px;

					&:hover {
						color: var(--primary);
					}
				}
			}
			&__day {
				border-radius: 50%;
				cursor: pointer;
				div {
					display: flex;
					justify-content: center;
				}
			}
			&__wrapper {
				visibility: hidden;
				position: fixed !important;
				z-index: 9999;
				margin-top: 0;
				background-color: var(--white);
				padding: 12px;
				box-shadow: 0px 0px 5px var(--gray-200);
				border-radius: 8px;
			}
			&__header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 5px;
			}
			&__prev {
				cursor: pointer;
				font-size: 22px;
			}
			&__next {
				cursor: pointer;
				font-size: 22px;
			}
			&__cal {
				border-spacing: 5px;

				thead {
					tr {
						color: var(--calendar-active);
						font-size: 13px;
						font-weight: 600;
					}
				}
			}
		}
	}
</style>
